import { Calendar } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  hasReminderToday,
  type Reminder,
  reminderAction,
  useCompleteReminder,
  useMyReminders,
} from '@/models/userReminder'

const DUE_CHECK_MS = 30_000
const DUE_GRACE_MS = 5 * 60 * 1000 // only notify for reminders that came due in the last 5 min

// Freshly due = not completed and due within the last DUE_GRACE_MS. The grace
// window keeps us from toasting long-past reminders (stale data, a backgrounded
// tab whose timer fired late, or old reminders pulled in by a refetch).
function isFreshlyDue(reminder: Reminder, now: number): boolean {
  if (reminder.completedAt) return false
  const due = new Date(reminder.reminderDate).getTime()
  return due <= now && due >= now - DUE_GRACE_MS
}

/**
 * Calendar nav button + a periodic check that toasts (bottom-right) when a
 * reminder comes due while the app is open (once per reminder). The toast
 * never auto-dismisses — it stays until the user marks the reminder complete.
 */
export function RemindersNavButton() {
  const navigate = useNavigate()
  const { reminders } = useMyReminders()
  const { mutate: completeReminder } = useCompleteReminder()
  const showDot = hasReminderToday(reminders)

  const notifiedRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    const check = () => {
      const now = Date.now()
      for (const reminder of reminders) {
        if (!isFreshlyDue(reminder, now) || notifiedRef.current.has(reminder.id)) continue
        notifiedRef.current.add(reminder.id)
        // Same CTA the reminder email carries — view the lead, start the
        // follow-up run, … — or none for a plain ad-hoc reminder.
        const cta = reminderAction(reminder)
        const toastId = `reminder-due-${reminder.id}`
        toast(reminder.message, {
          id: toastId,
          position: 'bottom-right',
          description: 'Reminder due',
          // Persist until the user completes it — reminders must never auto-dismiss.
          duration: Infinity,
          action: (
            <div className="ml-auto flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  completeReminder(reminder.id)
                  toast.dismiss(toastId)
                }}
              >
                Complete
              </Button>
              {cta && (
                <Button
                  size="sm"
                  onClick={() => {
                    navigate(cta.to)
                  }}
                >
                  {cta.label}
                </Button>
              )}
            </div>
          ),
        })
      }
    }
    check()
    const id = setInterval(check, DUE_CHECK_MS)
    return () => clearInterval(id)
  }, [reminders, navigate, completeReminder])

  return (
    <Button asChild variant="outline" size="icon" aria-label="My reminders" className="relative">
      <Link to="/insure/internal/reminders">
        <Calendar />
        {showDot && <span className="absolute top-1 right-1 size-2 rounded-full bg-blue-500" aria-hidden />}
      </Link>
    </Button>
  )
}
